import {
  NODE_CARD_STATE_COMPLETED,
  NODE_CARD_STATE_DEFAULT,
  NODE_CARD_STATE_FAILED,
  NODE_CARD_STATE_INVALID,
  NODE_CARD_STATE_ORPHAN,
  NODE_CARD_STATE_RUNDONE,
  NODE_CARD_STATE_RUNNING,
  NODE_CARD_STATE_SELECTED,
  NODE_CARD_STATE_SOURCE,
  NODE_CARD_STATE_TERMINAL,
  type NodeCardState,
} from '@aibuild/ui';
import type { ValidationError } from '../validation/types';
import type { NodeDisplayState } from '../EditorContext';

/**
 * Backend codes that mean "the node isn't wired correctly" rather than
 * "the node's config is broken". The design splits these into separate
 * visual states — invalid (red) for config, orphan (yellow) for wiring
 * — so the user knows whether to edit the node or wire it up.
 *
 * Reachability errors live on the orphan side because the node exists
 * but isn't on a path from any input to any output, which is a wiring
 * problem from the user's perspective.
 */
const ORPHAN_ERROR_KINDS: ReadonlySet<string> = new Set([
  'INPUT_PORT_DISCONNECTED',
  'OUTPUT_PORT_DISCONNECTED',
  'GATE_PORT_DISCONNECTED',
  'NODE_UNREACHABLE_FROM_INPUT',
  'NODE_CANNOT_REACH_OUTPUT',
]);

/**
 * Pure classifier for a single error → "is this a wiring (orphan) or a
 * config (invalid) issue?". Exported so tests + the run controls can
 * reuse the same definition.
 */
export function isOrphanError(error: ValidationError): boolean {
  return ORPHAN_ERROR_KINDS.has(String(error.kind));
}

/**
 * Inputs to {@link resolveNodeCardState}. Wrapped in an object so future
 * signals (drop-target, hovered-from-side-panel) can be added without
 * a positional-arg breaking change.
 */
export interface NodeCardStateInputs {
  readonly selected: boolean;
  readonly errors: readonly ValidationError[];
  /**
   * Findings that only block publishing. They can never turn the card red:
   * a half-wired draft is a normal authoring state, so the worst they do is
   * raise the amber orphan ring.
   */
  readonly publishHints?: readonly ValidationError[];
  readonly runState: NodeDisplayState;
  /**
   * Node kind for resolving the kind-specific resting halo: `'input'` →
   * {@link NODE_CARD_STATE_SOURCE} (primary halo at the start of the
   * chain), `'output'` → {@link NODE_CARD_STATE_TERMINAL} (green halo at
   * the end). Any other kind (or null) gets {@link NODE_CARD_STATE_DEFAULT}.
   *
   * Halos are only returned when no other state applies — selection,
   * run-state, and validation errors all replace them, mirroring the
   * design's L12934 clear rules.
   */
  readonly nodeKind?: 'input' | 'output' | null;
}

/**
 * Resolve the visual state ring the card should render. Mirrors the
 * design's CSS precedence:
 *
 *     invalid > orphan > runDone > running > selected > completed/failed > default
 *
 * The ranking matters because most state-driving signals overlap (a
 * node can be selected AND running AND completed in the persisted
 * snapshot), so the renderer needs a deterministic single output. We
 * give problem-states (invalid / orphan) priority over interaction
 * states (selected) so the user can't miss a wiring or config error
 * just by clicking the node.
 *
 * <ul>
 *   <li><b>invalid</b> — at least one validation error that is NOT a wiring
 *       error (e.g. missing instruction, missing agent).</li>
 *   <li><b>orphan</b> — at least one wiring error (orphan port, unreachable
 *       node) and no invalid errors, or a publish-only hint on a card that
 *       has no errors at all.</li>
 *   <li><b>runDone</b> — the live SSE-driven session reports
 *       {@code completed}. The bright fresh-green ring fades when the
 *       persisted snapshot replaces the live state.</li>
 *   <li><b>running</b> — live OR persisted reports {@code running}.</li>
 *   <li><b>failed</b> — live OR persisted reports {@code failed}.</li>
 *   <li><b>completed</b> — persisted snapshot reports {@code completed}
 *       (i.e. ran in a previous session).</li>
 *   <li><b>selected</b> — user clicked the card and there is no other
 *       state to surface.</li>
 *   <li><b>default</b> — resting card.</li>
 * </ul>
 */
export function resolveNodeCardState({
  selected,
  errors,
  publishHints,
  runState,
  nodeKind,
}: NodeCardStateInputs): NodeCardState {
  if (errors.length > 0) {
    const allOrphan = errors.every(isOrphanError);
    return allOrphan ? NODE_CARD_STATE_ORPHAN : NODE_CARD_STATE_INVALID;
  }
  if (publishHints && publishHints.length > 0) return NODE_CARD_STATE_ORPHAN;

  switch (runState.status) {
    case 'running':
      return NODE_CARD_STATE_RUNNING;
    case 'failed':
      return NODE_CARD_STATE_FAILED;
    case 'completed':
      // Live = freshly-finished green, persisted = subdued historical
      // green. The split mirrors the design's distinction between
      // `is-run-done` (bright halo) and `is-completed` (gentle outline).
      return runState.source === 'live'
        ? NODE_CARD_STATE_RUNDONE
        : NODE_CARD_STATE_COMPLETED;
    default:
      // 'idle' / 'waiting' / 'skipped' fall through to the interaction layer.
      break;
  }

  if (selected) return NODE_CARD_STATE_SELECTED;
  if (nodeKind === 'input') return NODE_CARD_STATE_SOURCE;
  if (nodeKind === 'output') return NODE_CARD_STATE_TERMINAL;
  return NODE_CARD_STATE_DEFAULT;
}
